import { Event } from "@garther/interfaces";
import {
  useCreateEventMutation,
  useCreateSessionMutation,
  useEventsInvalidation,
  useUpdateEventMutation,
  useUpdateEventStatusMutation,
  useUpsertEventQuestionsMutation
} from "@garther/query-hooks";
import { getErrorMessage, toast, useI18n } from "@garther/ui/libs";
import { useRouter } from "@tanstack/react-router";

export {
  eventsKeys,
  useEvents,
  useEvent,
  useEventsPagination,
  useEventsInvalidation,
  useBookingsByEvent as useBookingsByEventQuery,
  useEventSessions as useEventSessionsQuery,
  useTicketTierTemplates as useTicketTierTemplatesQuery,
  useEventQuestions as useEventQuestionsQuery,
  useEventHostStats,
  type SessionFilter
} from "@garther/query-hooks";

export function useCreateEvent(options: { onSuccess?: (result: Event) => void } = {}) {
  const { t } = useI18n();
  return useCreateEventMutation({
    onSuccess: options.onSuccess,
    onError: (error) => toast.error(getErrorMessage(error) || t("common.error.tryAgain"))
  });
}

export function useUpdateEvent(options: { onSuccess?: () => void } = {}) {
  const { t } = useI18n();
  const router = useRouter();
  return useUpdateEventMutation({
    onSuccess: () => {
      router.invalidate();
      options.onSuccess?.();
    },
    onError: (error) => toast.error(getErrorMessage(error) || t("common.error.tryAgain"))
  });
}

export function useUpdateEventStatus(options: { onSuccess?: () => void } = {}) {
  const { t } = useI18n();
  return useUpdateEventStatusMutation({
    onSuccess: options.onSuccess,
    onError: (error) => toast.error(getErrorMessage(error) || t("common.error.tryAgain"))
  });
}

export function useCreateSession(options: { onSuccess?: () => void } = {}) {
  const { t } = useI18n();
  return useCreateSessionMutation({
    onSuccess: () => options.onSuccess?.(),
    onError: (error) => toast.error(getErrorMessage(error) || t("common.error.tryAgain"))
  });
}

export function useUpsertEventQuestions(options: { onSuccess?: () => void } = {}) {
  const { t } = useI18n();
  const invalidate = useEventsInvalidation();
  return useUpsertEventQuestionsMutation({
    onSuccess: (_, { eventId }) => {
      invalidate.invalidateEventQuestions(eventId);
      options.onSuccess?.();
    },
    onError: (error) => toast.error(getErrorMessage(error) || t("common.error.tryAgain"))
  });
}
